/*
 * Brana Minimalistic 6.0.2 — commerce and editor interactions
 *
 * Consolidates the previously versioned Brana interaction patches while
 * preserving their established execution order. Runtime CSS custom properties are kept
 * only where geometry/animation values are measured or assigned by script.
 */

/* ============================================================
   Layout measurement
   ============================================================ */
(() => {
  const ready = () => {
    const body = document.body;
    if (!body || !body.classList.contains('bm-minimalistic')) return;

    const compact = window.matchMedia('(max-width: 767.98px)');
    let geometryFrame = 0;
    const setSectionGeometry = () => {
      geometryFrame = 0;
      const width = document.documentElement.clientWidth;
      const progress = Math.min(1, Math.max(0, (768 - width) / 268));
      const main = document.getElementById('MainContent');
      const firstContent = main && Array.from(main.children).find(node =>
        node instanceof HTMLElement && node.offsetHeight > 0 && !node.matches('script,style,link'));
      const writes = [];
      document.querySelectorAll('.bm-home-hero').forEach(hero => {
        const first = firstContent === hero || firstContent?.contains(hero);
        const offset = first ? Math.max(0, hero.getBoundingClientRect().top + window.scrollY) : 0;
        writes.push(() => hero.style.setProperty('--bm-hero-top-offset', `${offset.toFixed(2)}px`));
      });
      document.querySelectorAll('.bm-materials--mode-feature').forEach(section => {
        writes.push(() => section.style.setProperty('--bm-feature-offset-progress', progress.toFixed(5)));
      });
      document.querySelectorAll('[data-bm-counter-root]').forEach(section => {
        const active = compact.matches && !section.classList.contains('bm-vh-m--auto');
        const shell = section.querySelector('.bm-editorial-shell');
        if (!active || !shell) {
          writes.push(() => section.style.removeProperty('--bm-stat-compact'));
          return;
        }
        const style = getComputedStyle(section);
        const target = parseFloat(style.minHeight);
        const cells = [...section.querySelectorAll('.bm-stat')];
        const intro = section.querySelector('.bm-stats__intro');
        const space = name => parseFloat(style.getPropertyValue(`--bm-stat-fit-${name}`)) || 0;
        const savings = (intro ? space('intro') : 0) + cells.length * (space('top') + space('bottom'))
          + cells.filter(cell => cell.querySelector('.bm-stat__label')).length * space('label');
        const previous = parseFloat(style.getPropertyValue('--bm-stat-compact')) || 0;
        const natural = shell.getBoundingClientRect().height + (parseFloat(style.paddingTop) || 0)
          + (parseFloat(style.paddingBottom) || 0) + previous * savings;
        const fit = target > 0 && savings > 0 ? Math.min(1, Math.max(0, (natural - target) / savings)) : 0;
        writes.push(() => section.style.setProperty('--bm-stat-compact', fit.toFixed(4)));
      });
      writes.forEach(write => write());
    };
    const scheduleSectionGeometry = () => {
      if (!geometryFrame) geometryFrame = requestAnimationFrame(setSectionGeometry);
    };
    setSectionGeometry();
    window.addEventListener('resize', scheduleSectionGeometry, { passive: true });
    window.addEventListener('orientationchange', scheduleSectionGeometry, { passive: true });
    window.addEventListener('pageshow', scheduleSectionGeometry);
    document.fonts?.ready.then(scheduleSectionGeometry).catch(() => {});
    ['shopify:section:load','shopify:section:unload','shopify:section:reorder'].forEach(name =>
      document.addEventListener(name, scheduleSectionGeometry));

    /* Shared overlay top: announcement + visible header. */
    const setUiTop = () => {
      const header = document.querySelector('.section-header');
      if (!header) return;
      const rect = header.getBoundingClientRect();
      const bottom = Math.max(0, Math.min(window.innerHeight, rect.bottom));
      body.style.setProperty('--bm-ui-top', `${Math.round(bottom)}px`);
    };

    let uiRaf = 0;
    const scheduleUiTop = () => {
      if (uiRaf) cancelAnimationFrame(uiRaf);
      uiRaf = requestAnimationFrame(() => {
        uiRaf = 0;
        setUiTop();
      });
    };

    setUiTop();
    window.addEventListener('resize', scheduleUiTop, { passive: true });
    window.addEventListener('orientationchange', scheduleUiTop, { passive: true });
    window.addEventListener('scroll', scheduleUiTop, { passive: true });
    document.addEventListener('click', (event) => {
      if (event.target.closest('toggle-nav, toggle-menu, .mobile-menu-toggle, .header__icon--search, #filter-icon-buddle, close-button, back-button, #cart-icon-bubble')) {
        scheduleUiTop();
        setTimeout(scheduleUiTop, 70);
        setTimeout(scheduleUiTop, 320);
      }
    });

    let measuredHeader = null;
    const headerObserver = 'ResizeObserver' in window ? new ResizeObserver(() => { scheduleUiTop(); scheduleSectionGeometry(); }) : null;
    const observeHeader = () => {
      const header = document.querySelector('.section-header');
      if (header !== measuredHeader) {
        headerObserver?.disconnect();
        measuredHeader = header;
        if (header) headerObserver?.observe(header);
        document.querySelectorAll('.section-announcement-bar').forEach(node => headerObserver?.observe(node));
      }
      scheduleUiTop();
    };
    observeHeader();
    document.addEventListener('shopify:section:load', observeHeader);
    document.addEventListener('shopify:section:unload', scheduleUiTop);
    document.fonts?.ready.then(scheduleUiTop).catch(() => {});

  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ready, { once: true });
  } else {
    ready();
  }
})();

/* ============================================================
   Primary drawer coordination
   ============================================================ */
(() => {
  const init = async () => {
    if (!document.body?.classList.contains('bm-minimalistic')) return;

    const html = document.documentElement;

    const searchDrawer = () => document.querySelector('action-search.header-search');
    const cartDrawer = () => document.querySelector('cart-drawer');

    const syncPrimaryDrawerState = () => {
      const search = searchDrawer();
      const cart = cartDrawer();
      const primaryOpen = Boolean(
        search?.classList.contains('active') ||
        search?.hasAttribute('open') ||
        cart?.classList.contains('active')
      );
      html.classList.toggle('bm-primary-drawer-open', primaryOpen);
    };

    /* Search and mini cart are mutually exclusive. The stock cart already
       closes other canvases when it opens; Search did not reliably do so.
       Wrapping both entry points makes the contract symmetric and race-safe. */
    try {
      await customElements.whenDefined('action-search');
      const SearchElement = customElements.get('action-search');
      const proto = SearchElement?.prototype;
      if (proto?.openPopup && !proto.openPopup.__bmPrimaryDrawerWrapped) {
        const originalOpenPopup = proto.openPopup;
        const wrappedOpenPopup = function (...args) {
          const cart = cartDrawer();
          if (cart?.classList.contains('active') && typeof cart.close === 'function') {
            cart.close();
          }
          html.classList.add('bm-primary-drawer-open');
          const result = originalOpenPopup.apply(this, args);
          requestAnimationFrame(syncPrimaryDrawerState);
          return result;
        };
        wrappedOpenPopup.__bmPrimaryDrawerWrapped = true;
        proto.openPopup = wrappedOpenPopup;
      }

      if (proto?.close && !proto.close.__bmPrimaryDrawerWrapped) {
        const originalClose = proto.close;
        const wrappedClose = function (...args) {
          const result = originalClose.apply(this, args);
          requestAnimationFrame(syncPrimaryDrawerState);
          return result;
        };
        wrappedClose.__bmPrimaryDrawerWrapped = true;
        proto.close = wrappedClose;
      }
    } catch (_) {}

    try {
      await customElements.whenDefined('cart-drawer');
      const CartElement = customElements.get('cart-drawer');
      const proto = CartElement?.prototype;
      if (proto?.open && !proto.open.__bmPrimaryDrawerWrapped) {
        const originalOpen = proto.open;
        const wrappedOpen = function (...args) {
          const search = searchDrawer();
          if ((search?.classList.contains('active') || search?.hasAttribute('open')) && typeof search.close === 'function') {
            search.close();
          }
          html.classList.add('bm-primary-drawer-open');
          const result = originalOpen.apply(this, args);
          requestAnimationFrame(syncPrimaryDrawerState);
          return result;
        };
        wrappedOpen.__bmPrimaryDrawerWrapped = true;
        proto.open = wrappedOpen;
      }

      if (proto?.close && !proto.close.__bmPrimaryDrawerWrapped) {
        const originalClose = proto.close;
        const wrappedClose = function (...args) {
          const result = originalClose.apply(this, args);
          requestAnimationFrame(syncPrimaryDrawerState);
          return result;
        };
        wrappedClose.__bmPrimaryDrawerWrapped = true;
        proto.close = wrappedClose;
      }
    } catch (_) {}

    /* Class/open mutations also occur through generic theme close controls.
       Observe both drawers so the dimmer stacking state always follows them. */
    const observed = [searchDrawer(), cartDrawer()].filter(Boolean);
    if ('MutationObserver' in window && observed.length) {
      const observer = new MutationObserver(syncPrimaryDrawerState);
      observed.forEach((node) => observer.observe(node, {
        attributes: true,
        attributeFilter: ['class', 'open']
      }));
    }

    document.addEventListener('click', () => {
      requestAnimationFrame(syncPrimaryDrawerState);
      setTimeout(syncPrimaryDrawerState, 50);
      setTimeout(syncPrimaryDrawerState, 350);
    }, { passive: true });

    syncPrimaryDrawerState();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();

/* ============================================================
   Pagination windowing
   ============================================================ */
(() => {
  'use strict';

  const MAX_VISIBLE_CONTROLS = 6;
  let scheduled = false;

  function clampPaginationList(list) {
    const items = Array.from(list.children).filter((node) => node.tagName === 'LI');

    items.forEach((item) => item.classList.remove('bm-pagination-clamped-hidden'));
    if (items.length <= MAX_VISIBLE_CONTROLS) return;

    const navigationItems = items.filter((item) => item.querySelector('.pagination__item-arrow'));
    const pageItems = items.filter((item) => !item.querySelector('.pagination__item-arrow'));
    const availablePageSlots = Math.max(1, MAX_VISIBLE_CONTROLS - navigationItems.length);

    let chosenPages;
    const currentIndex = pageItems.findIndex((item) =>
      item.querySelector('[aria-current="page"], .pagination__item--current')
    );

    if (pageItems.length <= availablePageSlots) {
      chosenPages = pageItems;
    } else if (navigationItems.length === 1 && !items[0].querySelector('.pagination__item-arrow')) {
      // First page: keep the useful beginning plus Shopify's tail/ellipsis.
      chosenPages = [...pageItems.slice(0, 3), ...pageItems.slice(-2)];
    } else if (navigationItems.length === 1 && !items[items.length - 1].querySelector('.pagination__item-arrow')) {
      // Last page: symmetric treatment.
      chosenPages = [...pageItems.slice(0, 2), ...pageItems.slice(-3)];
    } else {
      // Middle pages: a compact window centred on the current page. Prev/Next
      // remain visible, so adjacent pages are the most useful four controls.
      const safeCurrent = currentIndex >= 0 ? currentIndex : 0;
      const before = Math.floor((availablePageSlots - 1) / 2);
      const maxStart = Math.max(0, pageItems.length - availablePageSlots);
      const start = Math.max(0, Math.min(safeCurrent - before, maxStart));
      chosenPages = pageItems.slice(start, start + availablePageSlots);
    }

    // Deduplicate edge selections while keeping the DOM's original order.
    const chosenSet = new Set(chosenPages);
    const visibleSet = new Set([...navigationItems, ...chosenSet]);

    // If deduplication left spare capacity, fill with the nearest remaining
    // page controls around the current item without ever exceeding six.
    if (visibleSet.size < MAX_VISIBLE_CONTROLS) {
      const currentDomIndex = currentIndex >= 0 ? currentIndex : 0;
      const candidates = pageItems
        .filter((item) => !visibleSet.has(item))
        .sort((a, b) => {
          const ai = pageItems.indexOf(a);
          const bi = pageItems.indexOf(b);
          return Math.abs(ai - currentDomIndex) - Math.abs(bi - currentDomIndex);
        });
      for (const candidate of candidates) {
        if (visibleSet.size >= MAX_VISIBLE_CONTROLS) break;
        visibleSet.add(candidate);
      }
    }

    items.forEach((item) => {
      item.classList.toggle('bm-pagination-clamped-hidden', !visibleSet.has(item));
    });
  }

  function clampAll(root = document) {
    if (root instanceof Element && root.matches('.pagination__list')) {
      clampPaginationList(root);
    }
    root.querySelectorAll?.('.pagination__list').forEach(clampPaginationList);
  }

  function scheduleClamp() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      clampAll(document);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleClamp, { once: true });
  } else {
    scheduleClamp();
  }

  // Collection/search filters replace result markup asynchronously. Observe the
  // document so newly returned pagination is clamped without coupling to one
  // particular facet/search implementation.
  const observer = new MutationObserver((mutations) => {
    if (mutations.some((mutation) => mutation.addedNodes.length || mutation.removedNodes.length)) {
      scheduleClamp();
    }
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();

/* Brana — Theme Editor interactions
   Counters and testimonials are small, dependency-free
   modules that re-initialize when Shopify reloads a section in the Theme Editor. */
(() => {
  'use strict';

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const formatCounter = (value, decimals) => {
    const locale = document.documentElement.lang || undefined;
    try {
      return new Intl.NumberFormat(locale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      }).format(value);
    } catch (_) {
      return decimals ? value.toFixed(decimals) : String(Math.round(value));
    }
  };

  const animateCounter = (node) => {
    if (node.dataset.bmCounterComplete === 'true') return;

    const rawTarget = node.dataset.bmCountTo;
    const target = Number(rawTarget);
    if (!Number.isFinite(target)) return;

    const decimalMatch = String(rawTarget).match(/\.(\d+)/);
    const decimals = decimalMatch ? decimalMatch[1].length : 0;
    node.dataset.bmCounterComplete = 'true';

    if (reducedMotion()) {
      node.textContent = formatCounter(target, decimals);
      return;
    }

    const duration = 1250;
    const startedAt = performance.now();

    const frame = (now) => {
      const progress = Math.min((now - startedAt) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const value = target * eased;
      node.textContent = formatCounter(decimals ? value : Math.round(value), decimals);

      if (progress < 1) {
        requestAnimationFrame(frame);
      } else {
        node.textContent = formatCounter(target, decimals);
      }
    };

    requestAnimationFrame(frame);
  };

  const counterObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          animateCounter(entry.target);
          observer.unobserve(entry.target);
        });
      }, { threshold: 0.28 })
    : null;

  const collect = (scope, selector) => {
    const nodes = [];
    if (scope instanceof Element && scope.matches(selector)) nodes.push(scope);
    if (scope.querySelectorAll) nodes.push(...scope.querySelectorAll(selector));
    return nodes;
  };

  const initCounters = (scope = document) => {
    collect(scope, '[data-bm-count-to]').forEach((node) => {
      if (node.dataset.bmCounterReady === 'true') return;
      node.dataset.bmCounterReady = 'true';

      if (counterObserver && !reducedMotion()) {
        counterObserver.observe(node);
      } else {
        animateCounter(node);
      }
    });
  };

  const initTestimonials = (scope = document) => {
    collect(scope, '[data-bm-testimonials]').forEach((root) => {
      if (root.dataset.bmTestimonialsReady === 'true') return;

      const slides = Array.from(root.querySelectorAll('[data-bm-testimonial-slide]'));
      if (!slides.length) return;

      const currentLabel = root.querySelector('[data-bm-testimonial-current]');
      const previousButton = root.querySelector('[data-bm-testimonial-prev]');
      const nextButton = root.querySelector('[data-bm-testimonial-next]');
      const autoplay = root.dataset.autoplay === 'true' && slides.length > 1 && !reducedMotion();
      const interval = Math.max(Number.parseInt(root.dataset.interval, 10) || 7000, 1000);
      let index = Math.max(slides.findIndex((slide) => !slide.hidden), 0);
      let timer = null;
      let paused = false;

      const render = () => {
        slides.forEach((slide, slideIndex) => {
          const active = slideIndex === index;
          slide.hidden = !active;
          slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        });
        if (currentLabel) currentLabel.textContent = String(index + 1).padStart(2, '0');
      };

      const stop = () => {
        if (!timer) return;
        window.clearInterval(timer);
        timer = null;
      };

      const start = () => {
        stop();
        if (!autoplay || paused) return;
        timer = window.setInterval(() => {
          index = (index + 1) % slides.length;
          render();
        }, interval);
      };

      const go = (nextIndex, restart = true) => {
        index = (nextIndex + slides.length) % slides.length;
        render();
        if (restart) start();
      };

      const pause = () => {
        paused = true;
        stop();
      };

      const resume = () => {
        paused = false;
        start();
      };

      previousButton?.addEventListener('click', () => go(index - 1));
      nextButton?.addEventListener('click', () => go(index + 1));
      root.addEventListener('mouseenter', pause);
      root.addEventListener('mouseleave', resume);
      root.addEventListener('focusin', pause);
      root.addEventListener('focusout', (event) => {
        if (!root.contains(event.relatedTarget)) resume();
      });

      root.bmTestimonials = {
        showSlide(slide) {
          const nextIndex = slides.indexOf(slide);
          if (nextIndex < 0) return;
          pause();
          go(nextIndex, false);
        },
        destroy() {
          stop();
          root.bmTestimonials = null;
          delete root.dataset.bmTestimonialsReady;
        }
      };

      root.dataset.bmTestimonialsReady = 'true';
      render();
      start();
    });
  };

  const initEditorialInteractions = (scope = document) => {
    initCounters(scope);
    initTestimonials(scope);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initEditorialInteractions(), { once: true });
  } else {
    initEditorialInteractions();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initEditorialInteractions(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    collect(event.target, '[data-bm-testimonials]').forEach((root) => root.bmTestimonials?.destroy());
  });

  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target.closest?.('[data-bm-testimonial-slide]');
    if (!slide) return;
    const root = slide.closest('[data-bm-testimonials]');
    root?.bmTestimonials?.showSlide(slide);
  });
})();

/* Brana policy pages — 5.19
   Shopify owns the policy source text. This layer removes duplicated template
   content and merchant-only notes, adds readable structure, standardizes legal
   contact details and supplies the concise shipping policy used by the store. */
(() => {
  const tools = document.querySelector('[data-bm-policy-tools]');
  const body = document.querySelector('.shopify-policy__body');
  const title = document.querySelector('.shopify-policy__title h1');
  if (!tools || !body || body.dataset.bmPolicyReady === 'true') return;

  const policyType = tools.dataset.policyType || 'generic';
  const config = {
    legalName: tools.dataset.legalName || '',
    legalEmail: tools.dataset.legalEmail || '',
    legalPhone: tools.dataset.legalPhone || '',
    legalAddress: tools.dataset.legalAddress || '',
    registrationNumber: tools.dataset.registrationNumber || '',
    vatNumber: tools.dataset.vatNumber || '',
    policyUpdatedDate: tools.dataset.policyUpdatedDate || '2. september 2026'
  };

  let contentRoot = body.firstElementChild?.classList.contains('rte')
    ? body.firstElementChild
    : body;

  const normalizedText = (value = '') => value.replace(/\s+/g, ' ').trim();

  const slugify = (value) => value
    .toLocaleLowerCase('da-DK')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/æ/g, 'ae')
    .replace(/ø/g, 'o')
    .replace(/å/g, 'a')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'afsnit';

  const topLevelChild = (node) => {
    let current = node;
    while (current?.parentElement && current.parentElement !== contentRoot) {
      current = current.parentElement;
    }
    return current?.parentElement === contentRoot ? current : null;
  };

  const replaceShippingPolicy = () => {
    if (policyType !== 'shipping') return;
    const template = tools.querySelector('[data-bm-shipping-policy-template]');
    if (!template) return;
    body.replaceChildren(template.content.cloneNode(true));
    contentRoot = body;
  };

  const splitInlineStrongPolicyBlocks = () => {
    if (policyType !== 'terms') return;

    Array.from(contentRoot.querySelectorAll('p')).forEach((paragraph) => {
      const nodes = Array.from(paragraph.childNodes);
      const strongCount = nodes.filter((node) =>
        node.nodeType === Node.ELEMENT_NODE && /^(STRONG|B)$/.test(node.tagName)
      ).length;
      if (strongCount < 8) return;

      const fragment = document.createDocumentFragment();
      let block = null;

      nodes.forEach((node) => {
        const startsBlock = node.nodeType === Node.ELEMENT_NODE && /^(STRONG|B)$/.test(node.tagName);
        if (startsBlock) {
          if (block && normalizedText(block.textContent)) fragment.appendChild(block);
          block = document.createElement('p');
          while (node.firstChild) block.appendChild(node.firstChild);
          return;
        }

        if (!block) {
          if (!normalizedText(node.textContent || node.nodeValue || '')) return;
          block = document.createElement('p');
        }
        block.appendChild(node);
      });

      if (block && normalizedText(block.textContent)) fragment.appendChild(block);
      paragraph.replaceWith(fragment);
    });
  };

  const removeRepeatedDocument = () => {
    const markerPatterns = {
      refund: [
        /^Vi har en 30-dages returpolitik/i,
        /^For at være kvalificeret til en returnering/i
      ],
      terms: [
        /Ansvarsfraskrivelse vedrørende generelle politikker/i,
        /^Velkommen til Esensera(?: Fine Aromatics)?!/i
      ]
    };
    const patterns = markerPatterns[policyType];
    if (!patterns) return;

    const candidates = Array.from(contentRoot.querySelectorAll('p, h2, h3, h4, li'));
    let duplicateStart = null;
    for (const pattern of patterns) {
      const matches = candidates.filter((node) => pattern.test(normalizedText(node.textContent)));
      if (matches.length > 1) {
        duplicateStart = topLevelChild(matches[1]);
        if (duplicateStart) break;
      }
    }
    if (!duplicateStart) return;

    let node = duplicateStart;
    while (node) {
      const next = node.nextSibling;
      node.remove();
      node = next;
    }
  };

  const removeTemplateDisclaimers = () => {
    if (policyType !== 'terms') return;
    const disclaimerPatterns = [
      /^Ansvarsfraskrivelse vedrørende generelle politikker/i,
      /^Denne skabelon kan oversættes/i,
      /^Læs disse materialer omhyggeligt/i,
      /^Oplysningerne i disse materialer afspejler/i,
      /^Brug af, adgang til eller overførsel af disse materialer/i
    ];
    contentRoot.querySelectorAll('p, li, blockquote').forEach((node) => {
      const text = normalizedText(node.textContent);
      if (disclaimerPatterns.some((pattern) => pattern.test(text))) node.remove();
    });
  };

  const normalizeRefundReturnInstructions = () => {
    if (policyType !== 'refund') return;
    const returnAddressPattern = /\[(?:INSERT|INDSÆT)\s+(?:RETURN|RETUR)(?:\s+ADDRESS|\s*ADRESSE)[^\]]*\]/i;
    const paragraph = Array.from(contentRoot.querySelectorAll('p')).find((node) =>
      returnAddressPattern.test(normalizedText(node.textContent))
    );
    if (!paragraph) return;

    paragraph.replaceChildren();
    paragraph.append(document.createTextNode('For at anmode om en returnering skal du kontakte os'));
    if (config.legalEmail) {
      paragraph.append(document.createTextNode(' på '));
      const link = document.createElement('a');
      link.href = `mailto:${config.legalEmail}`;
      link.textContent = config.legalEmail;
      paragraph.append(link);
    }
    paragraph.append(document.createTextNode('. Når returneringen er accepteret, sender vi returlabel, returadresse og instruktioner. Returvarer, der sendes uden en forudgående anmodning, accepteres ikke.'));

    Array.from(contentRoot.querySelectorAll('p')).forEach((node) => {
      if (node !== paragraph && /^Hvis din returnering accepteres/i.test(normalizedText(node.textContent))) {
        node.remove();
      }
    });
  };

  const replaceTextPlaceholders = () => {
    const replacements = [
      [/\[(?:INSERT|INDSÆT)\s+(?:TRADING|BUSINESS|COMPANY|VIRKSOMHEDS)\s*(?:NAME|NAVN)[^\]]*\]/gi, config.legalName],
      [/\[(?:INSERT|INDSÆT)\s+(?:BUSINESS|COMPANY|VIRKSOMHEDS)\s*(?:ADDRESS|ADRESSE)[^\]]*\]/gi, config.legalAddress],
      [/\[(?:INSERT|INDSÆT)\s+(?:EMAIL|E-MAIL)(?:\s+ADDRESS|\s+ADRESSE)?[^\]]*\]/gi, config.legalEmail],
      [/\[(?:INSERT|INDSÆT)\s+(?:WORK\s+)?(?:TELEPHONE|PHONE|TELEFON)(?:\s+NUMBER|\s+NUMMER)?[^\]]*\]/gi, config.legalPhone],
      [/\[(?:INSERT|INDSÆT)\s+(?:COMPANY|REGISTRATION|CVR|VIRKSOMHEDENS REGISTRERINGS)(?:\s+NUMBER|\s+NUMMER)?[^\]]*\]/gi, config.registrationNumber],
      [/\[(?:INSERT|INDSÆT)\s+(?:VAT|MOMS)(?:\s+NUMBER|\s+NUMMER)?[^\]]*\]/gi, config.vatNumber],
      [/\[(?:SHOP\s*OWNER|SHOPEJER)\]/gi, config.legalName]
    ];

    const walker = document.createTreeWalker(contentRoot, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach((node) => {
      let value = node.nodeValue;
      replacements.forEach(([pattern, replacement]) => {
        if (replacement) value = value.replace(pattern, replacement);
      });
      node.nodeValue = value;
    });
  };

  const replacePolicyLinkPlaceholders = () => {
    const walker = document.createTreeWalker(contentRoot, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);

    nodes.forEach((node) => {
      if (!/\[LINK\]/i.test(node.nodeValue || '')) return;
      const context = normalizedText(node.parentElement?.closest('p, li')?.textContent || node.nodeValue);
      let href = '';
      if (/(refusion|refund|retur|ombyt)/i.test(context)) href = '/policies/refund-policy';
      else if (/(persondata|privacy|privatliv)/i.test(context)) href = '/policies/privacy-policy';
      if (!href) return;

      const value = node.nodeValue;
      const marker = /(?:\bher\s*)?\[LINK\]/gi;
      const fragment = document.createDocumentFragment();
      let cursor = 0;
      let match;
      while ((match = marker.exec(value)) !== null) {
        fragment.append(document.createTextNode(value.slice(cursor, match.index)));
        const link = document.createElement('a');
        link.href = href;
        link.textContent = 'her';
        fragment.append(link);
        cursor = match.index + match[0].length;
      }
      fragment.append(document.createTextNode(value.slice(cursor)));
      node.replaceWith(fragment);
    });
  };

  const removeMerchantNotes = () => {
    const notePattern = /(NOTE\s+TO\s+(?:SHOP\s+OWNER|MERCHANT|STORE\s+OWNER)|BEMÆRKNING\s+TIL\s+(?:BUTIKSEJER|FORHANDLER|SHOP-EJER)|\[\s*NOTE\b)/i;
    const placeholderPattern = /\[(?:INSERT|INDSÆT)\b[^\]]*\]/i;
    contentRoot.querySelectorAll('p, li, blockquote, h2, h3, h4').forEach((node) => {
      const text = normalizedText(node.textContent);
      if (!text) return;
      if (notePattern.test(text) || placeholderPattern.test(text)) node.remove();
    });
  };

  const promotePolicyHeadings = () => {
    const refundHeadings = /^(Skader og problemer|Undtagelser\/?varer,? der ikke kan returneres|Ombytninger|EU['’]s 14-dages fortrydelsesperiode|Refusioner)$/i;

    Array.from(contentRoot.querySelectorAll('p')).forEach((paragraph) => {
      const text = normalizedText(paragraph.textContent);
      if (!text) return;

      const firstStrong = paragraph.firstElementChild;
      const strongText = firstStrong && /^(STRONG|B)$/.test(firstStrong.tagName)
        ? normalizedText(firstStrong.textContent)
        : '';
      const headingText = strongText || text;
      const isNumberedSection = /^(AFSNIT\s*\d+\s*[–—-]|OVERSIGT$)/i.test(headingText);
      const isRefundHeading = policyType === 'refund' && refundHeadings.test(headingText);
      if (!isNumberedSection && !isRefundHeading) return;

      const heading = document.createElement('h2');
      heading.textContent = headingText;
      paragraph.before(heading);

      if (!strongText) {
        paragraph.remove();
        return;
      }

      firstStrong.remove();
      const remaining = normalizedText(paragraph.textContent);
      if (!remaining) paragraph.remove();
    });
  };


  const replaceSectionAfterHeading = (heading, paragraphs) => {
    if (!heading) return;
    let cursor = heading.nextElementSibling;
    while (cursor && cursor.tagName !== 'H2') {
      const next = cursor.nextElementSibling;
      cursor.remove();
      cursor = next;
    }
    const fragment = document.createDocumentFragment();
    paragraphs.forEach((text) => {
      const paragraph = document.createElement('p');
      paragraph.textContent = text;
      fragment.appendChild(paragraph);
    });
    heading.after(fragment);
  };

  const tailorRefundPolicy = () => {
    if (policyType !== 'refund') return;

    const headings = Array.from(contentRoot.querySelectorAll('h2, h3'));
    const exceptionHeading = headings.find((heading) =>
      /(varer.*ikke kan returneres|undtagelser.*return|åbnede varer)/i.test(normalizedText(heading.textContent))
    );
    if (exceptionHeading) {
      exceptionHeading.textContent = 'Åbnede varer';
      replaceSectionAfterHeading(exceptionHeading, [
        'Aromatiske ekstrakter og olier kan ikke returneres under vores udvidede 30-dages returordning, når flasken har været åbnet, varen har været brugt, eller originalitetsringen eller forseglingen er brudt.',
        'Dette begrænser ikke dine lovbestemte rettigheder ved fejl eller mangler eller din lovbestemte fortrydelsesret.'
      ]);
    }

    const returnHeading = Array.from(contentRoot.querySelectorAll('h2, h3')).find((heading) =>
      /30\s*dages.*retur/i.test(normalizedText(heading.textContent))
    );
    if (returnHeading) {
      let cursor = returnHeading.nextElementSibling;
      while (cursor && cursor.tagName !== 'H2') {
        const text = normalizedText(cursor.textContent);
        if (cursor.tagName === 'P' && /samme stand.*modtagelsen|ubrugt.*original/i.test(text)) {
          cursor.textContent = 'Vores udvidede 30-dages returordning gælder for varer, der er ubrugte og uåbnede, ligger i den originale emballage og har ubrudt originalitetsring eller forsegling. Kvittering eller andet købsbevis skal vedlægges.';
          break;
        }
        cursor = cursor.nextElementSibling;
      }
    }

    const euHeading = Array.from(contentRoot.querySelectorAll('h2, h3')).find((heading) =>
      /14-dages.*fortrydelses/i.test(normalizedText(heading.textContent))
    );
    if (euHeading) {
      euHeading.textContent = "EU's 14-dages fortrydelsesret";
      replaceSectionAfterHeading(euHeading, [
        'Som forbruger har du som udgangspunkt ret til at fortryde købet inden for 14 dage efter, at du har modtaget varen, uden at angive en grund. Kontakt info@esensera.dk inden fristens udløb, hvis du vil gøre brug af fortrydelsesretten.',
        'Du må undersøge varen på samme måde, som du ville kunne i en fysisk butik. Hvis varen er håndteret mere end nødvendigt for at fastslå dens art, egenskaber og funktion, kan du hæfte for en eventuel værdiforringelse. Lovbestemte undtagelser gælder, hvor de finder anvendelse.'
      ]);
      const first = euHeading.nextElementSibling;
      if (first && config.legalEmail) {
        const value = first.textContent;
        const marker = config.legalEmail;
        const index = value.indexOf(marker);
        if (index >= 0) {
          const fragment = document.createDocumentFragment();
          fragment.append(document.createTextNode(value.slice(0, index)));
          const email = document.createElement('a');
          email.href = `mailto:${config.legalEmail}`;
          email.textContent = config.legalEmail;
          fragment.append(email, document.createTextNode(value.slice(index + marker.length)));
          first.replaceChildren(fragment);
        }
      }
    }
  };

  const ensurePaymentDetails = () => {
    if (policyType !== 'terms') return;
    const paymentHeading = Array.from(contentRoot.querySelectorAll('h2')).find((heading) =>
      /(priser.*betaling.*fakturering|afsnit\s*4.*priser|prices.*billing)/i.test(normalizedText(heading.textContent))
    );
    if (!paymentHeading) return;

    let sectionEnd = paymentHeading.nextElementSibling;
    while (sectionEnd && sectionEnd.tagName !== 'H2') sectionEnd = sectionEnd.nextElementSibling;

    const sectionNodes = [];
    let cursor = paymentHeading.nextElementSibling;
    while (cursor && cursor !== sectionEnd) {
      sectionNodes.push(cursor);
      cursor = cursor.nextElementSibling;
    }
    const sectionText = normalizedText(sectionNodes.map((node) => node.textContent).join(' '));

    if (!/\bVisa\b/i.test(sectionText) || !/Apple Pay/i.test(sectionText) || !/Google Pay/i.test(sectionText)) {
      const oldMethods = sectionNodes.find((node) =>
        /^(H3|H4)$/.test(node.tagName) && /betalingsmetoder/i.test(normalizedText(node.textContent))
      );
      if (oldMethods) {
        let node = oldMethods;
        while (node && node !== sectionEnd) {
          const next = node.nextElementSibling;
          if (node !== oldMethods && /^(H2|H3|H4)$/.test(node.tagName)) break;
          node.remove();
          node = next;
        }
      }

      const block = document.createElement('div');
      block.className = 'bm-policy-payment-methods';
      const heading = document.createElement('h3');
      heading.textContent = 'Betalingsmetoder';
      const intro = document.createElement('p');
      intro.textContent = 'Følgende betalingsmetoder er tilgængelige i webshoppen:';
      const list = document.createElement('ul');
      list.className = 'bm-policy-payment-methods__list';
      ['Visa', 'Mastercard', 'American Express', 'Dankort', 'Vipps', 'Swish', 'MobilePay', 'Apple Pay', 'Google Pay'].forEach((name) => {
        const item = document.createElement('li');
        item.textContent = name;
        list.appendChild(item);
      });
      const note = document.createElement('p');
      note.textContent = 'De betalingsmuligheder, der er tilgængelige for den konkrete ordre, vises i checkout.';
      block.append(heading, intro, list, note);

      const eanNode = sectionNodes.find((node) => /EAN-fakturering/i.test(normalizedText(node.textContent)));
      if (eanNode) eanNode.before(block);
      else if (sectionEnd) sectionEnd.before(block);
      else contentRoot.appendChild(block);
    }

    /* Imported policy HTML may already contain the complete methods list. Give
       that existing source the same skimmable presentation instead of only
       styling lists injected by the compatibility layer. */
    const methodsHeading = Array.from(contentRoot.querySelectorAll('h3, h4')).find((heading) =>
      /betalingsmetoder/i.test(normalizedText(heading.textContent))
    );
    if (methodsHeading) {
      let methodsBlock = methodsHeading.closest('.bm-policy-payment-methods');
      if (!methodsBlock) {
        methodsBlock = document.createElement('div');
        methodsBlock.className = 'bm-policy-payment-methods';
        methodsHeading.before(methodsBlock);
        let node = methodsHeading;
        while (node && node !== sectionEnd) {
          if (node !== methodsHeading && /^(H2|H3|H4)$/.test(node.tagName)) break;
          const next = node.nextElementSibling;
          methodsBlock.appendChild(node);
          node = next;
        }
      }
      methodsBlock.querySelector('ul, ol')?.classList.add('bm-policy-payment-methods__list');
    }

    let currentText = normalizedText(contentRoot.textContent);
    if (!/EAN-fakturering/i.test(currentText)) {
      const ean = document.createElement('div');
      ean.className = 'bm-policy-payment-note';
      const label = document.createElement('p');
      label.className = 'bm-policy-payment-note__label';
      label.textContent = 'EAN-fakturering';
      const copy = document.createElement('p');
      copy.textContent = 'Offentlige virksomheder og institutioner kan vælge EAN-fakturering i checkout. EAN-fakturering er forbeholdt offentlige kunder.';
      ean.append(label, copy);
      if (sectionEnd) sectionEnd.before(ean);
      else contentRoot.appendChild(ean);
      currentText = normalizedText(contentRoot.textContent);
    }

    if (!/kontooverførsel/i.test(currentText)) {
      const privilege = document.createElement('div');
      privilege.className = 'bm-policy-payment-note';
      const label = document.createElement('p');
      label.className = 'bm-policy-payment-note__label';
      label.textContent = 'Kontooverførsel for Esensera Privilege';
      const copy = document.createElement('p');
      copy.textContent = 'Esensera Privilege-kunder kan efter aftale betale via kontooverførsel. Betalingsoplysninger og eventuelle betalingsfrister fremgår af den konkrete aftale eller faktura.';
      privilege.append(label, copy);
      if (sectionEnd) sectionEnd.before(privilege);
      else contentRoot.appendChild(privilege);
    }
  };

  const compactIdentityPolicy = () => {
    if (!['contact', 'legal'].includes(policyType)) return;
    contentRoot.replaceChildren();

    const identity = document.createElement('div');
    identity.className = 'bm-policy-identity';

    const address = document.createElement('p');
    const addressLines = [];
    if (config.legalName) addressLines.push(config.legalName);
    if (config.legalAddress) {
      const parts = config.legalAddress
        .replace(/,\s+(?=\d{4}\b)/, '\n')
        .replace(/,\s+(?=Danmark\b)/i, '\n')
        .split(/\n+/)
        .map((part) => part.trim())
        .filter(Boolean);
      addressLines.push(...parts);
    }
    addressLines.forEach((value, index) => {
      if (index) address.appendChild(document.createElement('br'));
      address.append(document.createTextNode(value));
    });
    if (normalizedText(address.textContent)) identity.appendChild(address);

    const details = document.createElement('p');
    if (config.registrationNumber) {
      details.append(document.createTextNode(`CVR: ${config.registrationNumber}`));
      details.appendChild(document.createElement('br'));
    }
    const phone = document.createElement('a');
    phone.href = 'tel:+4593969394';
    phone.textContent = '(+45) 93 96 93 94';
    details.append(document.createTextNode('Telefon: '), phone);
    if (config.legalEmail) {
      details.appendChild(document.createElement('br'));
      const email = document.createElement('a');
      email.href = `mailto:${config.legalEmail}`;
      email.textContent = config.legalEmail;
      details.append(document.createTextNode('E-mail: '), email);
    }
    identity.appendChild(details);
    contentRoot.appendChild(identity);
  };

  const removeEmptyNodes = () => {
    contentRoot.querySelectorAll('p, li, h2, h3, h4').forEach((node) => {
      if (!normalizedText(node.textContent) && !node.querySelector('img, video, iframe')) node.remove();
    });
  };

  const ensurePolicyUpdatedMeta = () => {
    const date = normalizedText(config.policyUpdatedDate);
    if (!date) return null;

    const matches = Array.from(contentRoot.querySelectorAll('p')).filter((paragraph) =>
      /^Senest opdateret\s*:/i.test(normalizedText(paragraph.textContent))
    );
    const meta = matches.shift() || document.createElement('p');
    matches.forEach((duplicate) => duplicate.remove());

    meta.textContent = `Senest opdateret: ${date}`;
    meta.classList.add('bm-policy-meta');
    meta.classList.remove('bm-policy-meta--after-toc', 'bm-policy-meta--footer');

    if (!meta.isConnected) contentRoot.appendChild(meta);
    if (!['privacy', 'terms'].includes(policyType)) {
      meta.classList.add('bm-policy-meta--footer');
      contentRoot.appendChild(meta);
    }
    return meta;
  };

  const normalizePresentation = () => {
    if (title) {
      const current = normalizedText(title.textContent).toLocaleLowerCase('da-DK');
      const titleMap = {
        'refusionspolitik': 'Retur- og refusionspolitik',
        'refund policy': 'Retur- og refusionspolitik',
        'privacy policy': 'Politik om beskyttelse af persondata',
        'terms of service': 'Servicevilkår',
        'shipping policy': 'Leveringspolitik',
        'contact information': 'Kontaktoplysninger',
        'legal notice': 'Juridisk meddelelse'
      };
      if (titleMap[current]) title.textContent = titleMap[current];
    }

    const usedIds = new Set();
    contentRoot.querySelectorAll('h2, h3').forEach((heading, index) => {
      let id = heading.id || `policy-${slugify(heading.textContent)}`;
      const base = id;
      let suffix = 2;
      while (usedIds.has(id)) id = `${base}-${suffix++}`;
      usedIds.add(id);
      heading.id = id || `policy-afsnit-${index + 1}`;
      heading.classList.add('bm-policy-section-heading');
    });

    contentRoot.querySelectorAll('p').forEach((paragraph) => {
      const text = normalizedText(paragraph.textContent);
      if (!text) return;
      if (/^Senest opdateret\s*:/i.test(text)) paragraph.classList.add('bm-policy-meta');
      if (text.length > 110 || paragraph.querySelector('a, strong, b')) return;
      const letters = text.replace(/[^A-Za-zÆØÅæøå]/g, '');
      if (letters.length > 4 && text === text.toLocaleUpperCase('da-DK')) {
        paragraph.classList.add('bm-policy-eyebrow');
      }
    });

    if (['privacy', 'terms', 'refund'].includes(policyType)) {
      const firstParagraph = Array.from(contentRoot.querySelectorAll('p')).find((paragraph) =>
        normalizedText(paragraph.textContent) &&
        !paragraph.classList.contains('bm-policy-meta') &&
        !paragraph.classList.contains('bm-policy-eyebrow')
      );
      firstParagraph?.classList.add('bm-policy-lead');
    }

    if (config.legalEmail) {
      const email = config.legalEmail.toLocaleLowerCase('da-DK');
      const duplicateContactPattern = /^(Du kan altid kontakte os|Hvis du har spørgsmål om vores privatlivspraksis|Spørgsmål om servicevilkårene|Questions about these terms)/i;
      contentRoot.querySelectorAll('p').forEach((paragraph) => {
        const text = normalizedText(paragraph.textContent);
        const lower = text.toLocaleLowerCase('da-DK');
        if (text.length < 280 && lower.includes(email) && duplicateContactPattern.test(text)) {
          paragraph.remove();
        }
      });
    }
  };

  const buildPolicyToc = () => {
    if (!['terms', 'privacy'].includes(policyType)) return;
    const headings = Array.from(contentRoot.querySelectorAll('h2')).filter((heading) =>
      normalizedText(heading.textContent)
    );
    if (headings.length < 3) return;

    const nav = document.createElement('nav');
    nav.className = 'bm-policy-toc';
    nav.setAttribute('aria-label', 'Indholdsfortegnelse');

    const label = document.createElement('p');
    label.className = 'bm-policy-toc__label';
    label.textContent = 'Indhold';

    const links = document.createElement('div');
    links.className = 'bm-policy-toc__links';
    headings.forEach((heading) => {
      const link = document.createElement('a');
      link.href = `#${heading.id}`;

      const linkText = normalizedText(heading.textContent);
      const splitAt = linkText.lastIndexOf(' ');
      const prefix = splitAt > 0 ? linkText.slice(0, splitAt + 1) : '';
      const lastWord = splitAt > 0 ? linkText.slice(splitAt + 1) : linkText;
      if (prefix) link.append(document.createTextNode(prefix));

      const tail = document.createElement('span');
      tail.className = 'bm-policy-toc__tail';
      const word = document.createElement('span');
      word.className = 'bm-policy-toc__last-word';
      word.textContent = lastWord;
      const arrow = document.createElement('span');
      arrow.className = 'bm-link-arrow bm-link-arrow--down-right';
      arrow.setAttribute('aria-hidden', 'true');
      tail.append(word, arrow);
      link.appendChild(tail);
      links.appendChild(link);
    });

    nav.append(label, links);

    /* Long-policy navigation is the first object in the reading column. The
       update date occupies the quiet row immediately below it; on privacy the
       introduction then follows the date, and on terms the overview follows. */
    contentRoot.prepend(nav);
    const meta = contentRoot.querySelector('.bm-policy-meta');
    if (meta) {
      meta.classList.remove('bm-policy-meta--footer');
      meta.classList.add('bm-policy-meta--after-toc');
      nav.after(meta);
    }
  };

  replaceShippingPolicy();
  splitInlineStrongPolicyBlocks();
  removeRepeatedDocument();
  removeTemplateDisclaimers();
  normalizeRefundReturnInstructions();
  replaceTextPlaceholders();
  replacePolicyLinkPlaceholders();
  removeMerchantNotes();
  promotePolicyHeadings();
  tailorRefundPolicy();
  ensurePaymentDetails();
  compactIdentityPolicy();
  removeEmptyNodes();
  ensurePolicyUpdatedMeta();
  normalizePresentation();
  buildPolicyToc();
  body.dataset.bmPolicyType = policyType;
  body.dataset.bmPolicyReady = 'true';
})();
